import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button/Button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetOptionalGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import { PageLayout } from 'src/layouts';

import { useDeleteDataImportById } from '@/hooks/mutations/data-import';
import { useGetDataImportById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { dataImportUrl } from '@/utils/urls';

import { useTabs } from './useTabs';

type Props = {
  selectedTabId: 'details' | 'results';
};

const Page: FC<Props> = ({ selectedTabId }) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const { deleteDataImportById, loading: deleteLoading } =
    useDeleteDataImportById();
  const dataImportId = useGetOptionalGuidParam('dataImportId');
  const { data, error } = useGetDataImportById({
    queryArgs: { id: dataImportId ?? '' },
    shouldSkip: !dataImportId,
  });
  if (error) {
    throw error;
  }
  const { t: st } = useTranslation(['common'], { keyPrefix: 'dataImport' });
  const dataImport = data?.data_import[0];

  const tabs = useTabs(dataImport);
  const navigate = useNavigate();

  const title = dataImport?.Id ?? 'Add Data Import';

  // Object-level toolbar (CUS-173) — flag-gated. Surfaces the Save disc (bridged
  // from the details tab's DataImportForm via useObjectToolbarFormStore) and
  // relocates Delete into the overflow ⋮ menu's danger group. Delete tracks the
  // legacy visibility (shown only once a data import exists); it carries no
  // permission gate, matching the legacy button (parity).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title },
      save: true,
      actions: {
        danger: dataImport
          ? [
              {
                id: 'delete',
                label: st('deleteButton'),
                iconName: 'trash-01',
                onClick: () => setIsDeleteModalVisible(true),
              },
            ]
          : undefined,
      },
    }),
    [title, dataImport, st]
  );

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!dataImportId) {
        throw new Error('Missing data import id');
      }
      const result = await deleteDataImportById(dataImportId);
      if (result.delete_data_import?.affected_rows === 1) {
        setIsDeleted(true);
      }

      setIsDeleteModalVisible(false);
      navigate(dataImportUrl());

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  if (isDeleted) {
    return <></>;
  }

  if (data?.data_import.length === 0) {
    throw new PageNotFound(`Data import with id ${dataImportId} not found`);
  }

  return (
    <PageLayout
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          {dataImport && (
            <Button
              variant={'normal'}
              formAction={'none'}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('deleteButton')}
            </Button>
          )}
        </SpaceBetween>
      }
      title={title}
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={selectedTabId}
        variant={'container'}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => {
          setIsDeleteModalVisible(false);
        }}
      >
        {st('confirmDeleteMessage')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
